/**
 * iter413ap + iter413at + iter413au — Demo Mode Banner v3
 *
 * Single sticky band that cross-fades every 6s between two messages:
 *   🔴 "TOTO NENÍ FINÁLNÍ PRODUKT — DEMOVERZE"   (warning)
 *   🟢 "Prohlížíš si izolovaný sandbox · žádná tvoje data..."  (info)
 *
 * UX rules (user-requested):
 *   - No close (X) button — banner is mandatory while in demo
 *   - Smooth fade transition between bands (not abrupt)
 *   - After 5 min of session usage, show LICENSE LEAD CAPTURE modal
 *     (re-shows after 30 min if dismissed)
 *
 * License CTA ("Mám licenci →") still:
 *   - Extends TTL by +24h
 *   - Redirects to /admin/login?utm_source=demo&utm_medium=banner&utm_campaign=license_cta
 *   - Logs telemetry
 */
import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Sparkles, Clock, PlayCircle, AlertTriangle, ArrowRight, ShieldCheck, X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_V1 } from "../config/api";

const MODAL_AFTER_MS = 5 * 60 * 1000;      // 5 minutes
const MODAL_REOPEN_AFTER_MS = 30 * 60 * 1000; // 30 minutes after dismiss
const ROTATE_MS = 6000;

function fmtRemaining(seconds) {
  if (!seconds || seconds < 0) return "vypršelo";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function DemoBanner() {
  const { user } = useAuth();
  const [remaining, setRemaining] = useState(0);
  const [face, setFace] = useState(0); // 0 = warning, 1 = sandbox info
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // ─── TTL countdown ────────────────────────────────────────────────────
  useEffect(() => {
    if (user?.role !== "demo") return;
    const expires = user.demo_expires_at;
    if (!expires) return;
    const update = () => {
      const ms = new Date(expires).getTime() - Date.now();
      setRemaining(Math.max(0, Math.floor(ms / 1000)));
    };
    update();
    const id = setInterval(update, 30_000);
    return () => clearInterval(id);
  }, [user]);

  // ─── Rotation of the message face every ROTATE_MS ─────────────────────
  useEffect(() => {
    if (user?.role !== "demo") return;
    const id = setInterval(() => setFace((f) => (f + 1) % 2), ROTATE_MS);
    return () => clearInterval(id);
  }, [user]);

  // ─── Schedule the lead-capture modal after MODAL_AFTER_MS ─────────────
  useEffect(() => {
    if (user?.role !== "demo") return;
    const last = parseInt(localStorage.getItem("demo_lead_modal_dismissed_at") || "0", 10);
    const sinceDismiss = Date.now() - last;
    const delay = last > 0 && sinceDismiss < MODAL_REOPEN_AFTER_MS
      ? MODAL_REOPEN_AFTER_MS - sinceDismiss
      : MODAL_AFTER_MS;
    const t = setTimeout(() => setModalOpen(true), Math.max(delay, 5000));
    return () => clearTimeout(t);
  }, [user]);

  const handleLicenseClick = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (licenseLoading) return;
    setLicenseLoading(true);
    try {
      axios.post(`${API_V1}/demo/telemetry`, {
        event: "license_cta_click",
        meta: { from: "demo_banner" },
      }, { withCredentials: true }).catch(() => {});
      const r = await axios.post(`${API_V1}/demo/extend`, {
        reason: "license_cta",
        utm_source: "demo",
        utm_medium: "banner",
        utm_campaign: "license_cta",
      }, { withCredentials: true }).catch(() => null);
      if (r?.data?.extended) {
        toast.success("Bonus +24h k demu — máš víc času na rozhodnutí 💪");
      }
    } finally {
      setLicenseLoading(false);
    }
    window.location.href = "/admin/login?utm_source=demo&utm_medium=banner&utm_campaign=license_cta";
  }, [licenseLoading]);

  if (user?.role !== "demo") return null;

  // ─── Faces ────────────────────────────────────────────────────────────
  const Face1 = (
    <div className="flex items-center justify-center gap-2 text-white"
         data-testid="demo-face-warning">
      <AlertTriangle className="w-4 h-4 shrink-0 animate-pulse" />
      <span className="font-black tracking-[0.18em] uppercase text-[11px] sm:text-sm">
        Toto není finální produkt &nbsp;·&nbsp; DEMOVERZE
      </span>
    </div>
  );

  const Face2 = (
    <div className="flex items-center gap-2 sm:gap-3 text-slate-900"
         data-testid="demo-face-info">
      <ShieldCheck className="w-4 h-4 shrink-0" />
      <span className="font-medium text-[11px] sm:text-xs md:text-sm truncate">
        Prohlížíš si izolovaný sandbox <strong>{user.demo_company_name || "Demo"}</strong> · žádná tvá data nejsou viditelná pro Solar Green ani jiné uživatele.
      </span>
    </div>
  );

  return (
    <>
      <div
        data-testid="demo-mode-banner-wrap"
        className="sticky top-0 z-[60] shadow-md overflow-hidden"
        style={{
          background: face === 0
            ? "linear-gradient(90deg, #dc2626 0%, #ef4444 50%, #dc2626 100%)"
            : "linear-gradient(90deg, #10b981 0%, #34d399 50%, #22d3ee 100%)",
          transition: "background 1100ms ease-in-out",
        }}
      >
        <div data-testid="demo-mode-banner"
             className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center gap-3 text-xs sm:text-sm">
          {/* Crossfade of the two faces */}
          <div className="relative flex-1 min-w-0">
            <div className={`transition-opacity duration-1000 ${face === 0 ? "opacity-100" : "opacity-0 absolute inset-0"}`}>
              {Face1}
            </div>
            <div className={`transition-opacity duration-1000 ${face === 1 ? "opacity-100" : "opacity-0 absolute inset-0"}`}>
              {Face2}
            </div>
          </div>

          {/* TTL + CTAs always visible on the right */}
          <span className={`inline-flex items-center gap-1.5 font-semibold whitespace-nowrap transition-colors ${
            face === 0 ? "text-white" : "text-slate-900"
          }`}>
            <Clock className="w-3.5 h-3.5" />
            {fmtRemaining(remaining)}
          </span>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("demo-tour:restart"))}
            data-testid="demo-tour-restart-btn"
            title="Spustit interaktivní prohlídku znovu"
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold whitespace-nowrap transition-colors ${
              face === 0
                ? "bg-white/20 hover:bg-white/30 text-white"
                : "bg-slate-900/15 hover:bg-slate-900/25 text-slate-900"
            }`}>
            <PlayCircle className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Prohlídka</span>
          </button>
          <button
            onClick={handleLicenseClick}
            disabled={licenseLoading}
            data-testid="demo-license-cta"
            title="Prodlouží demo o +24h a odešle tě na přihlášení"
            className={`hidden md:inline-flex items-center gap-1 px-3 py-1 rounded-lg text-[11px] font-bold transition-colors ${
              face === 0
                ? "bg-slate-900 text-red-300 hover:bg-slate-800"
                : "bg-slate-900 text-emerald-300 hover:bg-slate-800"
            } disabled:opacity-60`}>
            {licenseLoading ? "..." : <>Mám licenci <ArrowRight className="w-3 h-3" /></>}
          </button>
        </div>
      </div>

      {modalOpen && (
        <LeadCaptureModal
          companyName={user.demo_company_name}
          onClose={() => {
            localStorage.setItem("demo_lead_modal_dismissed_at", String(Date.now()));
            setModalOpen(false);
          }}
          onLicense={handleLicenseClick}
        />
      )}
    </>
  );
}


// ─── Lead Capture Modal (shown after 5 minutes of usage) ──────────────────
function LeadCaptureModal({ companyName, onClose, onLicense }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Vyplň prosím jméno a e-mail");
      return;
    }
    setSubmitting(true);
    try {
      // Log as hot lead + telemetry
      axios.post(`${API_V1}/demo/telemetry`, {
        event: "lead_capture_submit",
        meta: form,
      }, { withCredentials: true }).catch(() => {});
      // Push to leads endpoint (no-auth via demo whitelist)
      await axios.post(`${API_V1}/demo/lead`, form, {
        withCredentials: true,
      }).catch(() => {});
      toast.success("Díky! Ozveme se ti do 24h.");
      localStorage.setItem("demo_lead_modal_dismissed_at", String(Date.now()));
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="demo-lead-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-sm animate-in fade-in duration-300"
    >
      <div className="relative max-w-md w-full bg-slate-900 rounded-2xl border border-emerald-500/30 p-6 shadow-2xl shadow-emerald-500/10">
        <button
          onClick={onClose}
          data-testid="demo-lead-modal-close"
          className="absolute top-4 right-4 text-slate-400 hover:text-white"
          aria-label="Zavřít"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 grid place-items-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-bold">Líbí se ti to?</div>
        </div>

        <h3 className="text-xl font-bold text-white mb-1">Nech kontakt — připravíme ti vlastní CRM</h3>
        <p className="text-sm text-slate-400 mb-5">
          {companyName ? <>Pro <strong className="text-emerald-300">{companyName}</strong>. </> : null}
          Žádný spam — ozveme se do 24h s návrhem podle tvého oboru.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Jméno a příjmení *"
            data-testid="demo-lead-name"
            className="w-full px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400"
          />
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="E-mail *"
            data-testid="demo-lead-email"
            className="w-full px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400"
          />
          <input
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="Telefon (nepovinné)"
            data-testid="demo-lead-phone"
            className="w-full px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400"
          />
          <textarea
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            placeholder="Co řešíš ve své firmě? (nepovinné)"
            rows={3}
            data-testid="demo-lead-message"
            className="w-full px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400 resize-none"
          />

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onLicense}
              data-testid="demo-lead-modal-license"
              className="flex-1 px-3 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-emerald-300 text-sm font-semibold inline-flex items-center justify-center gap-1.5"
            >
              Mám licenci <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="demo-lead-submit"
              className="flex-1 px-3 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
            >
              {submitting ? "..." : <>Odeslat <ArrowRight className="w-3.5 h-3.5" /></>}
            </button>
          </div>
        </form>

        <p className="text-[10px] text-slate-500 mt-3 text-center">
          Příští připomenutí za 30 minut, pokud teď zavřeš.
        </p>
      </div>
    </div>
  );
}
